/**
 * The capture session — the non-React orchestration behind every shot (SV-2-2 / SV-2-3):
 *
 *   beginShot ──► (chain genesis · geo snapshot · attempt++ · queue row)
 *   appendChunk ─► sha256 · h_i · IMU window · geo header ──► IndexedDB row ──► uploader.kick()
 *   seal ────────► optional WebAuthn assertion over h_n ──► shot sealed row ──► uploader.kick()
 *   abandon ─────► drop this attempt's rows (a failed edge gate, a retake)
 *
 * The uploader runs in the background from `start()`; its events are mirrored into the store so
 * the UI shows pending / offline / refused honestly and the flow returns to a refused shot.
 * Nothing here blocks the capture UI: every step is async and the queue is the buffer.
 */

import { completeShot, putChunk, type ScriptShot, type ShotSignature } from '@/api/capture';
import { exchangeLinkToken, isOtpChallenge } from '@/api/client';
import { useCaptureStore } from '@/store/captureStore';
import { chunkKey, shotKey, type QueuePersistence, type QueueStorage, type StoredShot } from '@/upload/chunkQueue';
import { Uploader, type UploaderDeps } from '@/upload/uploader';
import { ShotChain, type ChunkLink } from './chainHash';
import { ImuTracer, imuHeader } from './imu';
import { geoHeader, latestFix, latestGeoHeader, type GeoFix } from './shotGeo';
import { registerPlatformCredential, signChainHead, type SignatureUnavailableReason } from './webauthnSign';

export interface ShotHandle {
  readonly shot: ScriptShot;
  readonly attempt: number;
  readonly chain: ShotChain;
  /**
   * The fix held when the shot began — bound (as X-SV-Geo) into EVERY chunk of this shot, so the
   * viewfinder's LocationCard can freeze to exactly what the chain carries (DEC-095 C1; SPEC_STAMP_ROUND
   * §C.1 rule 1). Null when no fix was held at shot start: then each chunk binds the latest fix at the
   * moment it is appended (a fix arriving mid-clip is still evidence) and the card stays live.
   */
  readonly boundFix: GeoFix | null;
  appendChunk(blob: Blob | Uint8Array, capturedAt: string, imuWindow?: { from: number; to: number }): Promise<ChunkLink>;
  seal(info: { mimeType: string; durationS: number | null }): Promise<{ finalChainHash: string; chunkCount: number; signed: boolean }>;
  abandon(): Promise<void>;
}

export interface CaptureSession {
  readonly visitId: string;
  readonly nonce: string;
  readonly persistence: QueuePersistence;
  readonly uploader: Uploader;
  readonly storage: QueueStorage;
  readonly imu: ImuTracer;
  beginShot(shot: ScriptShot): Promise<ShotHandle>;
  /** Skip an OPTIONAL shot — a local navigation fact; the server simply never receives it. */
  skipShot(shot: ScriptShot): void;
  /** Register the device signing key once (must be called from a user gesture on some browsers). */
  ensureSigningKey(rpName: string): Promise<void>;
  dispose(): void;
}

export interface SessionOptions {
  readonly visitId: string;
  readonly nonce: string;
  readonly storage: QueueStorage;
  readonly persistence: QueuePersistence;
  /** Override the wire functions (tests) — defaults to the typed §2 client. */
  readonly uploaderDeps?: Partial<Omit<UploaderDeps, 'storage'>>;
  readonly imu?: ImuTracer;
  /** Called when the bearer dies mid-upload; default re-exchanges the stored link token. */
  readonly reauth?: () => Promise<boolean>;
}

/** Blob → bytes with a FileReader fallback (older Safari, and jsdom, lack Blob.arrayBuffer). */
export async function blobToBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') return new Uint8Array(await blob.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('blob read failed'));
    reader.readAsArrayBuffer(blob);
  });
}

let current: CaptureSession | null = null;

export function currentSession(): CaptureSession | null {
  return current;
}

export function setCurrentSessionForTests(s: CaptureSession | null): void {
  current = s;
}

async function defaultReauth(): Promise<boolean> {
  const token = useCaptureStore.getState().linkToken;
  if (!token) return false;
  try {
    const out = await exchangeLinkToken(token);
    // An OTP-fronted link cannot be re-exchanged silently mid-upload: the code goes to the representative's
    // email/phone and only the front door can take it. Report "not re-authed" — the queue keeps the chunks
    // (IndexedDB) and the next visit through /v/:linkToken resumes them after the code is entered.
    return !isOtpChallenge(out);
  } catch {
    return false;
  }
}

export async function createCaptureSession(opts: SessionOptions): Promise<CaptureSession> {
  const store = useCaptureStore;
  const imu = opts.imu ?? new ImuTracer();
  const uploader = new Uploader({
    storage: opts.storage,
    putChunk,
    completeShot,
    ...(opts.uploaderDeps ?? {}),
  });
  const reauth = opts.reauth ?? defaultReauth;
  let signingKey: 'unknown' | 'ready' | SignatureUnavailableReason = 'unknown';

  const refreshPending = async (): Promise<void> => {
    store.getState().setPendingChunks(await uploader.pending());
  };

  const off = uploader.on((e) => {
    const s = store.getState();
    switch (e.type) {
      case 'chunk-uploaded':
        void refreshPending();
        break;
      case 'shot-complete':
        s.markCompleted(e.shotId);
        void refreshPending();
        break;
      case 'shot-refused':
        s.unsealLocal([e.shotId]);
        s.markRefused(e.shotId, { code: e.code, message: e.message });
        void refreshPending();
        break;
      case 'offline':
        s.setOnline(false);
        break;
      case 'online':
        s.setOnline(true);
        break;
      case 'auth-required':
        void reauth().then((ok) => {
          if (ok) uploader.kick();
        });
        break;
      default:
        break;
    }
  });

  const session: CaptureSession = {
    visitId: opts.visitId,
    nonce: opts.nonce,
    persistence: opts.persistence,
    uploader,
    storage: opts.storage,
    imu,

    async ensureSigningKey(rpName) {
      if (signingKey !== 'unknown') return;
      const r = await registerPlatformCredential(opts.visitId, rpName);
      if ('unavailable' in r) {
        signingKey = r.unavailable;
        store.getState().setSignature({ state: 'unavailable', reason: r.unavailable });
      } else {
        signingKey = 'ready';
        store.getState().setSignature({ state: 'available' });
      }
    },

    async beginShot(shot) {
      const key = shotKey(opts.visitId, shot.shotId);
      const prev = await opts.storage.getShot(key);
      const attempt = (prev?.attempt ?? 0) + 1;
      if (prev) await opts.storage.deleteShotAttempt(opts.visitId, shot.shotId, prev.attempt);
      const row: StoredShot = {
        key,
        visitId: opts.visitId,
        shotId: shot.shotId,
        ordinal: shot.ordinal,
        attempt,
        promptKey: shot.promptKey,
        kind: shot.kind,
        mimeType: '',
        chunkCount: null,
        finalChainHash: null,
        durationS: null,
        signature: null,
        status: 'recording',
        refusalCode: null,
        refusalMessage: null,
        serverSha256: null,
        completeAttempts: 0,
        updatedAt: Date.now(),
      };
      await opts.storage.putShot(row);
      store.getState().clearRefused(shot.shotId);
      // X-SV-Prompt-Id = the script shotId (be rule); the chain binds the same string.
      const chain = new ShotChain(opts.nonce, shot.shotId);
      await chain.genesis();
      // The fix held at shot start binds every chunk of the shot ("where the device said it was when the
      // shot began" — shotGeo.ts). A shot that begins with no fix binds the latest per chunk instead.
      const boundFix = latestFix();
      const boundGeo = boundFix ? geoHeader(boundFix) : null;
      // No position request here (US-03482 G2, 2026-11-03): the capture's ONE shared watch keeps `latestFix()` fresh —
      // a per-shot getCurrentPosition was a new acquisition (a new prompt on Chrome for iOS) on every step.

      const handle: ShotHandle = {
        shot,
        attempt,
        chain,
        boundFix,
        async appendChunk(blob, capturedAt, imuWindow) {
          const bytes = ArrayBuffer.isView(blob) ? (blob as Uint8Array) : await blobToBytes(blob as Blob);
          const imuSummary = imuWindow ? imu.window(imuWindow.from, imuWindow.to) : null;
          const imuStr = imuSummary ? imuHeader(imuSummary) : null;
          const geo = boundGeo ?? latestGeoHeader();
          const link = await chain.append(bytes, { capturedAt, geo, imu: imuStr });
          await opts.storage.putChunk({
            key: chunkKey(opts.visitId, shot.shotId, attempt, link.seq),
            visitId: opts.visitId,
            shotId: shot.shotId,
            ordinal: shot.ordinal,
            attempt,
            seq: link.seq,
            bytes: bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
            sha256: link.chunkSha256,
            chainHash: link.chainHash,
            headers: { capturedAt, promptId: shot.shotId, geo, imu: imuStr },
            status: 'pending',
            attempts: 0,
            lastError: null,
            enqueuedAt: Date.now(),
          });
          uploader.kick();
          void refreshPending();
          return link;
        },
        async seal(info) {
          const finalChainHash = chain.headHash as string;
          const chunkCount = chain.count;
          let signature: ShotSignature | null = null;
          if (signingKey === 'ready') {
            const r = await signChainHead(finalChainHash);
            if ('type' in r) signature = { type: r.type, payload: r.payload };
          }
          await opts.storage.updateShot(key, {
            status: 'sealed',
            mimeType: info.mimeType,
            chunkCount,
            finalChainHash,
            durationS: info.durationS,
            signature,
          });
          store.getState().markSealedLocal(shot.shotId);
          uploader.kick();
          return { finalChainHash, chunkCount, signed: signature !== null };
        },
        async abandon() {
          await opts.storage.deleteShotAttempt(opts.visitId, shot.shotId, attempt);
          await opts.storage.updateShot(key, { status: 'abandoned' });
          void refreshPending();
        },
      };
      return handle;
    },

    skipShot(shot) {
      if (shot.mandatory) return;
      store.getState().markSkipped(shot.shotId);
    },

    dispose() {
      off();
      uploader.stop();
      imu.stop();
      if (current === session) current = null;
    },
  };

  uploader.start(opts.visitId);
  await refreshPending();
  current = session;
  return session;
}
